'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Heart, MapPin, Star, Users, Clock } from 'lucide-react';

const featuredVenues = [
{
  id: 'venue-001',
  name: 'The Grand Meridian',
  location: 'Bandra West, Mumbai',
  city: 'Mumbai',
  type: 'Wedding',
  rating: 4.9,
  reviews: 312,
  capacity: 800,
  priceHourly: 15000,
  priceFullDay: 95000,
  image: "https://images.unsplash.com/photo-1628428989068-ba3fdd65ed79",
  alt: 'Luxurious grand ballroom with golden chandeliers and white draped walls',
  tag: 'Most Booked',
  tagColor: 'bg-red-600',
  amenities: ['Catering', 'Parking', 'AV Setup', 'Decor'],
  available: true
},
{
  id: 'venue-002',
  name: 'Skyline Terrace',
  location: 'Connaught Place, Delhi',
  city: 'Delhi',
  type: 'Corporate',
  rating: 4.7,
  reviews: 184,
  capacity: 200,
  priceHourly: 8000,
  priceFullDay: 48000,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1654f53e2-1766856410437.png",
  alt: 'Modern rooftop terrace venue with city skyline view and minimalist decor',
  tag: 'New',
  tagColor: 'bg-green-600',
  amenities: ['WiFi', 'AV Setup', 'Catering', 'Parking'],
  available: true
},
{
  id: 'venue-003',
  name: 'Emerald Gardens',
  location: 'Whitefield, Bangalore',
  city: 'Bangalore',
  type: 'Reception',
  rating: 4.8,
  reviews: 256,
  capacity: 500,
  priceHourly: 12000,
  priceFullDay: 72000,
  image: "https://images.unsplash.com/photo-1696178002041-5508192f675a",
  alt: 'Lush garden venue with fairy lights strung between trees and floral arches',
  tag: 'Top Rated',
  tagColor: 'bg-yellow-600',
  amenities: ['Garden', 'Catering', 'Parking', 'Decor'],
  available: true
},
{
  id: 'venue-004',
  name: 'Crystal Pavilion',
  location: 'Jubilee Hills, Hyderabad',
  city: 'Hyderabad',
  type: 'Wedding',
  rating: 4.6,
  reviews: 198,
  capacity: 600,
  priceHourly: 18000,
  priceFullDay: 110000,
  image: "https://images.unsplash.com/photo-1574660146489-bc8e6b1ff34d",
  alt: 'Crystal-adorned wedding pavilion with dramatic lighting and ornate stage setup',
  tag: 'Premium',
  tagColor: 'bg-purple-600',
  amenities: ['Catering', 'Valet', 'Stage', 'AV Setup'],
  available: false
},
{
  id: 'venue-005',
  name: 'The Blue Lagoon',
  location: 'Koregaon Park, Pune',
  city: 'Pune',
  type: 'Birthday',
  rating: 4.5,
  reviews: 143,
  capacity: 150,
  priceHourly: 5000,
  priceFullDay: 30000,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1df6d2bea-1769691418494.png",
  alt: 'Vibrant indoor party venue with colorful lighting and spacious dance floor',
  tag: 'Budget Pick',
  tagColor: 'bg-blue-600',
  amenities: ['DJ', 'Bar', 'Catering', 'Parking'],
  available: true
},
{
  id: 'venue-006',
  name: 'Heritage Haveli',
  location: 'Civil Lines, Jaipur',
  city: 'Jaipur',
  type: 'Wedding',
  rating: 4.9,
  reviews: 421,
  capacity: 1000,
  priceHourly: 25000,
  priceFullDay: 150000,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0d261bd-1766896152808.png",
  alt: 'Traditional Rajasthani haveli with intricate carved arches and vibrant courtyard',
  tag: 'Heritage',
  tagColor: 'bg-amber-600',
  amenities: ['Heritage', 'Catering', 'Valet', 'Decor'],
  available: true
}];


export default function FeaturedVenues() {
  const [wishlisted, setWishlisted] = useState<Set<string>>(new Set());

  const toggleWishlist = (id: string) => {
    setWishlisted((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else
      next.add(id);
      return next;
    });
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="section-title">Featured Venues</h2>
            <p className="section-subtitle">Handpicked premium spaces for your next event</p>
          </div>
          <Link
            href="/venue-listing-page"
            className="hidden md:inline-flex items-center gap-1.5 text-red-600 font-semibold text-sm hover:underline">
            
            View all venues →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {featuredVenues.map((venue) =>
          <article key={venue.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden card-hover shadow-card group">
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <AppImage
                src={venue.image}
                alt={venue.alt}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500" />
              
                {/* Tag */}
                <div className={`absolute top-3 left-3 ${venue.tagColor} text-white text-xs font-bold px-2.5 py-1 rounded-full`}>
                  {venue.tag}
                </div>
                {/* Availability */}
                <div className={`absolute top-3 right-12 text-xs font-semibold px-2.5 py-1 rounded-full ${
              venue.available ? 'badge-available' : 'badge-booked'}`
              }>
                  {venue.available ? 'Available' : 'Booked'}
                </div>
                {/* Wishlist */}
                <button
                onClick={() => toggleWishlist(venue.id)}
                className="absolute top-3 right-3 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-sm transition-all active:scale-90"
                aria-label={`${wishlisted.has(venue.id) ? 'Remove from' : 'Add to'} wishlist`}>
                
                  <Heart
                  size={16}
                  className={wishlisted.has(venue.id) ? 'fill-red-500 text-red-500' : 'text-gray-500'} />
                
                </button>
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-bold text-gray-900 text-lg leading-tight">{venue.name}</h3>
                  <div className="flex items-center gap-1 shrink-0">
                    <Star size={14} className="fill-yellow-400 text-yellow-400" />
                    <span className="text-sm font-bold text-gray-900 font-tabular">{venue.rating}</span>
                    <span className="text-xs text-gray-400">({venue.reviews})</span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 text-gray-500 text-sm mb-3">
                  <MapPin size={13} className="text-red-400 shrink-0" />
                  <span className="truncate">{venue.location}</span>
                </div>

                {/* Amenities */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {venue.amenities.map((a) =>
                <span key={`${venue.id}-amenity-${a}`} className="bg-gray-50 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-md border border-gray-100">
                      {a}
                    </span>
                )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Clock size={11} />
                      <span>₹{venue.priceHourly.toLocaleString('en-IN', { useGrouping: true })}/hr</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-500">Up to {venue.capacity.toLocaleString('en-IN', { useGrouping: true })} guests</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400 mb-0.5">Full day from</div>
                    <div className="text-red-600 font-extrabold text-lg font-tabular">
                      ₹{(venue.priceFullDay / 1000).toFixed(0)}K
                    </div>
                  </div>
                </div>

                <Link
                href="/venue-details-page"
                className="mt-4 w-full btn-primary justify-center text-sm py-2.5">
                
                  View Details
                </Link>
              </div>
            </article>
          )}
        </div>

        <div className="text-center mt-10 md:hidden">
          <Link href="/venue-listing-page" className="btn-outline text-sm">
            View All Venues
          </Link>
        </div>
      </div>
    </section>);

}